'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Anchor, Box, Button, Space, Stack, Text, Title } from '@mantine/core'
import { usePrevious } from '@mantine/hooks'
import { KnockLogo } from '@phoenix-web/icons-images'
import { SignedInNotice } from '@phoenix-web/react-shared/rsc-components'
import { Audience } from '@phoenix-web/utils/journey'
import { useMutation } from '@phoenix-web/ux-gql/apollo'
import { CONSUME_INVITE_TOKEN } from '@/app/_components/auth/useConsumeInviteToken'
import AuthLandingLayout from '@/app/_wrappers/AuthLandingLayout'
import {
  authApiPaths,
  COURTYARD2_HOME_DASHBOARD_PATH,
  LOGOUT_WITH_REDIRECT_TO_COURTYARD2_SIGNUP,
  TERRACE_SIGNUP_URL,
} from '@/constants'
import { authSignupContent } from '@/text'
import AccountSignupForm from './AccountSignUpForm'

const { LOGIN_TEXT, LOGIN_LINK_LABEL, WELCOME_TITLE, TITLE_AUDIENCE_PICKER, TITLE } = authSignupContent

export default function AuthSignupContent({
  signedIn,
  signedInEmail,
}: {
  /** Keyed off `sub`, not email: a profile can have a session and no email. */
  signedIn?: boolean
  signedInEmail?: string
}) {
  // The session is resolved on the server and passed down, so there is no
  // client profile fetch and nothing renders before we know the answer.
  const isAuthenticated = !!signedIn

  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  // With app router, these values should exist on first render
  const returnTo = searchParams?.get('returnTo')
  const returnToSearchQuery = returnTo ? `returnTo=${encodeURIComponent(returnTo)}` : ''
  const audience = searchParams?.get('audience')

  const invitationUUID = searchParams?.get('invitation_uuid')

  const [consumeInviteToken] = useMutation(CONSUME_INVITE_TOKEN)

  const [hasInviteBearerRun, setHasInviteBearerRun] = useState(false)

  // An invitation token still redirects: the visitor followed an invite to join
  // a specific account, so consuming it and moving on is what they asked for.
  // Arriving signed in *without* one is the ambiguous case, and that now renders
  // SignedInNotice instead of silently bouncing to the dashboard.
  useEffect(() => {
    if (isAuthenticated && invitationUUID && !hasInviteBearerRun && consumeInviteToken) {
      setHasInviteBearerRun(true) // prevent multiple executions for a component mount
      consumeInviteToken({
        variables: {
          input: { token: invitationUUID },
        },
        context: {
          clientName: 'proxy',
        },
      })
        .then(() => {
          router.push(COURTYARD2_HOME_DASHBOARD_PATH)
        })
        .catch((err) => {
          // If it fails we'll just redirect anyway
          console.error(err)
          router.push(COURTYARD2_HOME_DASHBOARD_PATH)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, invitationUUID, router, consumeInviteToken])

  const isHomebuyer = audience === Audience.HOMEBUYER
  const [hideSignup, setHideSignup] = useState(!isHomebuyer)

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams ? searchParams.toString() : '')
      params.set(name, value)

      return params.toString()
    },
    [searchParams]
  )

  const previousAudienceValue = usePrevious(audience)
  useEffect(() => {
    if (previousAudienceValue !== undefined && previousAudienceValue !== audience) {
      setHideSignup(!audience)
    }
  }, [previousAudienceValue, audience])

  const handleSelection = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setHideSignup(false)
    const target = e.currentTarget
    router.push(`${pathname}?${createQueryString('audience', target.value)}`)
  }

  const showAudienceSelector = !isHomebuyer && hideSignup
  // Rendered in whichever branch is showing. An invitation token is mid-redirect,
  // so the notice would only flash.
  const signedInNotice =
    isAuthenticated && !invitationUUID ? (
      <SignedInNotice email={signedInEmail} logoutHref={LOGOUT_WITH_REDIRECT_TO_COURTYARD2_SIGNUP} />
    ) : null

  return (
    <>
      {showAudienceSelector ? (
        <>
          <Box>
            <KnockLogo height="26" />
          </Box>
          <Space h="xxl" />
          <Space h="xxl" />
          <Space h="xxl" />
          <Title size={48} pb="sm" c="brand-blue.9" style={{ textAlign: 'center' }}>
            {WELCOME_TITLE}
          </Title>
          {/* Sits under the heading, next to the choice it affects. Constrained
              because this branch renders full-bleed, unlike the form column below. */}
          {signedInNotice && (
            <Box maw={420} w="100%" m="auto">
              {signedInNotice}
            </Box>
          )}
          {/* Audience Selector */}
          <Box m="auto" w="16.25rem">
            <Text pb="sm" fw="700" fz="1.125rem" ta="center">
              {TITLE_AUDIENCE_PICKER}
            </Text>
            <Space h="lg" />
            <Stack gap="sm">
              <Button
                type="button"
                h="3.125rem"
                variant="outline"
                color="dark.9"
                radius="var(--mantine-radius-sm)"
                bd="1.5px solid black"
                value={Audience.HOMEBUYER}
                onClick={(e) => handleSelection(e)}
              >
                I&rsquo;m a homebuyer
              </Button>
              <Button
                component="a"
                h="3.125rem"
                type="button"
                variant="outline"
                color="dark.9"
                radius="var(--mantine-radius-sm)"
                bd="1.5px solid black"
                href={TERRACE_SIGNUP_URL + `?${createQueryString('audience', Audience.AGENT)}`}
              >
                I&rsquo;m an agent
              </Button>
              <Button
                component="a"
                type="button"
                h="3.125rem"
                variant="outline"
                color="dark.9"
                radius="var(--mantine-radius-sm)"
                bd="1.5px solid black"
                href={TERRACE_SIGNUP_URL + `?${createQueryString('audience', Audience.LENDER)}`}
              >
                I&rsquo;m a lender
              </Button>
            </Stack>
            <Space h="xxl" />
          </Box>
        </>
      ) : (
        // Signup Form
        <AuthLandingLayout>
          <Box maw="350px" w={{ base: '90%', md: '60%' }}>
            {signedInNotice}
            <Text fz="1.125rem" fw={700} ta="center">
              {TITLE}
            </Text>{' '}
            <Space h="xl" />
            <AccountSignupForm />
            <Space h="lg" />
            {/* "Already have an account? Log in" is noise for someone who is
                already in one — the notice above offers the action they need.
                It also carries the gap under Cancel, so replace that height
                rather than letting the button sit against the page edge. */}
            {isAuthenticated ? (
              <Space h="xl" />
            ) : (
              <Text size="sm">
                {LOGIN_TEXT}{' '}
                <Anchor fs="none" href={`${authApiPaths.LOGIN}?${returnToSearchQuery}`}>
                  {LOGIN_LINK_LABEL}
                </Anchor>
              </Text>
            )}
          </Box>
        </AuthLandingLayout>
      )}
    </>
  )
}
